import type { CarrierMedium } from "kontrolEdge/helpers/carrierAccess";
import type { ManualLegBody } from "./requests/request";

/**
 * The Carrier Access of an Extended PoP site (CTX-830): its legs grouped by
 * the sold service (84-) they back, and the legs no service claimed yet.
 * Pure, so the grouping is tested without rendering.
 */

export type LegSource = "lm" | "carr" | "manual";

/** The sold Carrier Access a leg backs, as the EP detail serves it. */
export interface LegCarrierAccess {
  id: number;
  serviceId: string;
  name: string | null;
  accessType: string | null;
  status: string;
}

/** A leg of the site, flat, with the CPE it reaches. */
export interface SiteLeg {
  id: number;
  cpeId: number;
  carrierCode: string;
  name: string;
  source: LegSource;
  circuitId: string | null;
  serviceRequestId: string | null;
  /** fiber | satellite, null while nobody captured it (CTX-800). */
  medium?: CarrierMedium | null;
  /** false = no router description names it anymore. */
  observed: boolean;
  carrierAccess: LegCarrierAccess | null;
}

export interface SiteCarrierAccess extends LegCarrierAccess {
  legs: SiteLeg[];
}

export interface SiteCarrierAccessGroups {
  accesses: SiteCarrierAccess[];
  /** Legs no Carrier Access claimed: what a declaration can take. */
  freeLegs: SiteLeg[];
}

export const groupSiteCarrierAccess = (
  legs: SiteLeg[]
): SiteCarrierAccessGroups => {
  const byId = new Map<number, SiteCarrierAccess>();
  const freeLegs: SiteLeg[] = [];

  for (const leg of legs) {
    if (!leg.carrierAccess) {
      freeLegs.push(leg);
      continue;
    }
    const access = byId.get(leg.carrierAccess.id) ?? {
      ...leg.carrierAccess,
      legs: [],
    };
    access.legs.push(leg);
    byId.set(access.id, access);
  }

  return {
    accesses: Array.from(byId.values()).sort((a, b) =>
      a.serviceId.localeCompare(b.serviceId)
    ),
    freeLegs,
  };
};

const SOURCE_LABEL: Record<LegSource, string> = {
  lm: "Last mile",
  carr: "Carrier",
  manual: "Manual",
};

/** How the leg got here: read from a marker, or declared by hand. */
export const legSourceLabel = (source: string): string =>
  SOURCE_LABEL[source as LegSource] ?? source;

/** The Carrier Access detail of P3, addressed by id. */
export const carrierAccessDetailPath = (id: number): string =>
  `/kontrol/carrier-access-details?id=${id}`;

/** Only a Global Admin declares or changes a Carrier Access. */
export const canManageCarrierAccess = (tenantType?: string | null): boolean =>
  tenantType === "GLOBAL_ADMIN";

/** The message the API answered with, else a generic one. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === "string" && message ? message : fallback;
};

/** A manual leg being filled in a form. */
export interface ManualLegDraft {
  cpeId: number | null;
  portAllocationId: number | null;
  carrierCode: string;
  circuitId: string;
  serviceRequestId: string;
  medium: CarrierMedium | null;
}

export const EMPTY_MANUAL_LEG: ManualLegDraft = {
  cpeId: null,
  portAllocationId: null,
  carrierCode: "",
  circuitId: "",
  serviceRequestId: "",
  medium: null,
};

/** The request body for a draft, or null while it is incomplete. */
export const manualLegBody = (draft: ManualLegDraft): ManualLegBody | null => {
  const carrierCode = draft.carrierCode.trim().toUpperCase();
  if (!draft.cpeId || !draft.portAllocationId || !carrierCode) return null;
  return {
    cpeId: draft.cpeId,
    portAllocationId: draft.portAllocationId,
    carrierCode,
    ...(draft.circuitId.trim() ? { circuitId: draft.circuitId.trim() } : {}),
    ...(draft.serviceRequestId.trim()
      ? { serviceRequestId: draft.serviceRequestId.trim() }
      : {}),
    ...(draft.medium ? { medium: draft.medium } : {}),
  };
};
